import { useMemo } from 'react';
import { compact } from 'lodash';
import { useAllSpecialRequirementsQuery } from '../../graphql';

export function useSpecialRequirements(): Map<
  number,
  { shipIds: Set<number>; locationIds: Set<number>; name: string }
> {
  const { data } = useAllSpecialRequirementsQuery({
    fetchPolicy: 'cache-and-network',
  });

  return useMemo(() => {
    if (!data?.allSpecialRequirements) {
      return new Map();
    }

    return new Map(
      compact(data.allSpecialRequirements.nodes).map((specialRequirement) => {
        const name = specialRequirement.name;
        // Extract ship IDs from vessel fulfills requirement associations
        const shipIds = new Set<number>();
        compact(
          specialRequirement
            .vesselFulfillsRequirementAssociationsBySpecialRequirementId?.nodes,
        ).forEach((vesselAssoc) => {
          // Add direct vessel ID if it exists
          if (vesselAssoc.vesselId) {
            shipIds.add(vesselAssoc.vesselId);
          }
          // Add ship IDs from vessel attributes associations
          compact(
            vesselAssoc.vesselAttributeByVesselAttributeId
              ?.vesselAttributesAssociationsByVesselAttributeId?.nodes,
          ).forEach(({ shipId }) => shipIds.add(shipId));
          // Add ship IDs from primary attribute
          compact(
            vesselAssoc.vesselAttributeByVesselAttributeId
              ?.shipsByPrimaryAttributeId?.nodes,
          ).forEach(({ id }) => shipIds.add(id));
        });

        // Extract location IDs from location requirement associations
        const locationIds = new Set<number>();
        compact(
          specialRequirement
            .locationRequirementAssociationsBySpecialRequirementId?.nodes,
        ).forEach((locationAssoc) => {
          // Add direct location ID
          if (locationAssoc.locationId) {
            locationIds.add(locationAssoc.locationId);
          }
          // Add location IDs from zone locations
          compact(
            locationAssoc.zoneByZoneId?.zoneLocationsByZoneId?.nodes,
          ).forEach((zoneLocation) => {
            if (zoneLocation.locationId) {
              locationIds.add(zoneLocation.locationId);
            }
          });
        });

        return [specialRequirement.id, { shipIds, locationIds, name }];
      }),
    );
  }, [data]);
}
